import { useState } from 'react';
import { useQuery, useQueryClient } from '@tanstack/react-query';
import { toast } from 'sonner';
import { FileText, Loader2, RefreshCw } from 'lucide-react';
import { Button } from '@/shared/components/ui/button';
import { useProfile } from '@/shared/auth/SessionProvider';
import { regenerarFacturaData } from '@/features/administracion/api';
import { buildFacturaArcaPdfFile } from '@/features/administracion/pdf/facturaArca';
import { uploadDocument, type EntityType } from '@/shared/api/documents';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog';
import { FileLink } from '@/shared/components/file-upload/FileLink';
import { useActiveCountry } from '@/shared/countries/ActiveCountryProvider';
import { cn } from '@/shared/lib/cn';
import { parseMoney, formatMoney } from '@/shared/lib/money';
import { cajaBase } from '@/shared/lib/cajasEfectivo';
import {
  listActiveComprobantesByOperacion,
  listComprobantesByOperacion,
  type ComprobantePagoItem,
} from '@/shared/api/controllers/comprobantePagos';
import { calcSaldoAFacturar, cuentaParaTechoFacturacion, montoNcEnMonedaVenta } from '../lib/calc';
import type { Moneda } from './MoneyCell';
import { MoneyCell } from './MoneyCell';
import { GenerarNotaCreditoDialog } from './GenerarNotaCreditoDialog';

type StageFilter = 'final' | 'anticipada' | 'ambas';

/**
 * "Ver Facturas" — read-only list of `04.ComprobantePagos` rows for
 * a venta that have an attached factura. Mirrors PowerApps's
 * `PopUp_ViewFacturasCargadas` (Screen_DetalleVenta), which collects
 * `Collect_VentaFacturaPagos` with the filter:
 *
 *   Status_CP = "Confirmado" AND ContieneFactura_CP = "SI"
 *     AND IDOperacion_CP = ToViewSelectedVenta
 *
 * No edit affordances — that's a future Facturación-module concern.
 *
 * MX two-stage variant: when a cobro is an Anticipo that carries
 * factura, admin uploads **both** a Factura Anticipada and a Factura
 * Final. We surface each as its own row with a "Etapa" column so
 * operators can audit both PDFs. AR rows (and MX pago-final cobros)
 * stay single-row — the Etapa column hides itself when no Anticipada
 * is present in the dataset.
 */
export function VerFacturasDialog({
  ventaId,
  moneda,
  ventaTotal,
  onClose,
}: {
  ventaId: string;
  moneda: Moneda;
  /** Venta grand total (in `moneda`) — used for the "saldo a facturar" summary. */
  ventaTotal?: number;
  onClose: () => void;
}) {
  const comprobantesQ = useQuery({
    queryKey: ['pagos', 'comprobantes', ventaId],
    queryFn: () => listActiveComprobantesByOperacion(ventaId),
    staleTime: 30_000,
  });

  // The Anticipada/Final two-stage flow is an MX-only feature. In AR a
  // cobro never sets `EsPagoFinal_CP` (it's an MX flag), so every AR row
  // would otherwise look like an "anticipo" and surface the MX selector.
  const { code } = useActiveCountry();
  const isMx = code === 'MX';
  const isAr = code === 'AR';
  // Which factura row (if any) has the "Generar NC" dialog open.
  const [ncFor, setNcFor] = useState<{ id: string; nro: string } | null>(null);

  // Regenerar factura (AR, admin-tier): rebuild the representación impresa with
  // the N° de Serie / Despacho now loaded at the despacho stage, and re-upload
  // it over the factura slot. Print-only — the CAE/número are untouched.
  const qc = useQueryClient();
  const profile = useProfile();
  const canRegenerar =
    isAr && (profile?.perfil === 'admin' || profile?.perfil === 'administracion');
  const [regeneratingId, setRegeneratingId] = useState<string | null>(null);
  async function handleRegenerar(receiptId: string, entityType: EntityType): Promise<void> {
    setRegeneratingId(receiptId);
    try {
      const res = await regenerarFacturaData(receiptId);
      if (res.outcome !== 'ok') {
        toast.warning('No se pudo regenerar la factura', {
          description: 'message' in res ? res.message : 'Faltan datos para regenerarla.',
        });
        return;
      }
      const file = await buildFacturaArcaPdfFile(res.factura);
      await uploadDocument({ entityType, entityId: receiptId, file });
      await qc.invalidateQueries({ queryKey: ['pagos', 'comprobantes', ventaId] });
      toast.success('Factura regenerada', {
        description: 'Se actualizó el PDF con el N° de Serie / Despacho.',
      });
    } catch (err) {
      toast.error('No se pudo regenerar la factura', {
        description: err instanceof Error ? err.message : 'Error desconocido',
      });
    } finally {
      setRegeneratingId(null);
    }
  }

  // NC rows are excluded from `listActiveComprobantesByOperacion`, so pull the
  // full list (AR only) to net partial Notas de Crédito out of "Facturado".
  const ncQ = useQuery({
    queryKey: ['pagos', 'comprobantes-full', ventaId],
    queryFn: () => listComprobantesByOperacion(ventaId),
    staleTime: 30_000,
    enabled: isAr,
  });

  // AR: facturas anuladas por una NC completa. Un full NC reabre su factura
  // (limpia Facturado_CP para que vuelva a la cola de Facturación) — las
  // listamos como historial "factura → anulada por NC" y las sacamos de
  // `facturadas` para que el monto acreditado vuelva al saldo a facturar.
  // Se arma desde la lista COMPLETA (ncQ) para tomar el nº de cada NC y para
  // detectar el padre aunque su `contiene_factura` haya quedado limpio (datos
  // viejos previos al fix de 2026-07-22). Sólo padres YA no facturados: una NC
  // parcial deja el padre Facturado_CP='SI' y sigue en la tabla normal.
  const fullList = ncQ.data ?? [];
  const byPk = new Map<string, ComprobantePagoItem>();
  for (const c of fullList) {
    if (c.fields.IdInterno_CP) byPk.set(c.fields.IdInterno_CP, c);
  }
  const ncsByParent = new Map<string, ComprobantePagoItem[]>();
  for (const c of fullList) {
    const parentPk = c.fields.NotaCreditoDeId_CP;
    if (c.fields.Status_P === 'Nota de Credito' && parentPk) {
      const arr = ncsByParent.get(parentPk) ?? [];
      arr.push(c);
      ncsByParent.set(parentPk, arr);
    }
  }
  const anuladasPorNc: Array<{ parent: ComprobantePagoItem; ncs: ComprobantePagoItem[] }> = [];
  for (const [parentPk, ncs] of ncsByParent) {
    const parent = byPk.get(parentPk);
    if (parent && parent.fields.Facturado_CP !== 'SI') anuladasPorNc.push({ parent, ncs });
  }
  const anuladasParentPks = new Set(
    anuladasPorNc.map((a) => a.parent.fields.IdInterno_CP).filter(Boolean) as string[],
  );

  // Fuente: la lista COMPLETA (`ncQ`), no la activa. `listActiveComprobantes­ByOperacion`
  // descarta los 'Anulado' antes de que los veamos, y con Facturación Previa
  // una factura viva puede quedar sobre un cobro anulado: así no aparecía acá
  // (imposible generarle la Nota de Crédito que la revierte — justo la acción
  // que la alerta de CAE huérfanos manda a hacer) y encima `sumFacturado` la
  // ignoraba, devolviendo su monto al saldo a facturar. Reaplicamos las demás
  // exclusiones de la lista activa y levantamos sólo la de 'Anulado', para las
  // filas que siguen teniendo factura emitida.
  const comprobantesParaFacturas = (
    isAr ? ncQ.data ?? comprobantesQ.data ?? [] : comprobantesQ.data ?? []
  ).filter((c) => cuentaParaTechoFacturacion(c.fields));

  const facturadas = comprobantesParaFacturas.filter(
    (c) =>
      // Confirmado O ya facturado: con Facturación Previa (AR) el cobro tiene
      // factura emitida mientras sigue en 'Pendiente de Confirmacion' /
      // 'Pendiente de Cobro'. Sin esta rama la factura no se ve acá (no se
      // puede imprimir ni generarle NC) y además `sumFacturado` la ignora, con
      // lo cual el saldo a facturar queda inflado y se puede facturar dos veces
      // la misma plata.
      ((c.fields.Status_P ?? '') === 'Confirmado' ||
        c.fields.Facturado_CP === 'SI') &&
      c.fields.ContieneFactura_CP === 'SI' &&
      // Reopened-by-full-NC cobros keep contiene=SI now, but they're history,
      // not a live factura — surfaced in the "anuladas" section instead.
      !anuladasParentPks.has(c.fields.IdInterno_CP ?? ''),
  );
  // Saldo a facturar (informativo) = total de la venta − Σ ya facturado. Reads
  // each cobro's amount in the venta currency (Pesos_CP for USD medios on an
  // ARS venta, else Monto_CP), same as the row's MoneyCell. A full NC undoes
  // its cobro's factura (facturado=false → sale de `facturadas`), so ese monto
  // vuelve al saldo automáticamente.
  const sumFacturado = facturadas.reduce((acc, c) => {
    const f = c.fields;
    const medio = cajaBase(f.MedioPago_CP ?? ''); // caja base, ver lib/cajasEfectivo
    const isUsdMedio =
      cajaBase(medio) === 'Efectivo Dolar' ||
      medio === 'Transferencia Galicia Dolar' ||
      medio === 'Transferencia Dolar';
    const amt =
      moneda === 'ARS' && isUsdMedio
        ? parseMoney(f.Pesos_CP) ?? parseMoney(f.Monto_CP) ?? 0
        : parseMoney(f.Monto_CP) ?? 0;
    return acc + amt;
  }, 0);
  // Partial NCs reduce how much of the venta is really facturado. A FULL NC
  // reopens its factura (it leaves `facturadas` entirely), so we subtract an NC
  // ONLY when its parent factura is still attached — otherwise we'd double-count,
  // since the reopened factura's whole amount already left `sumFacturado`. Match
  // by raw PK (`NotaCreditoDeId_CP` → parent `IdInterno_CP`). Committed NCs keep
  // status 'Nota de Credito'; rejected/overflowed ones are neutralized to 'Anulado'.
  const attachedFacturaPks = new Set(
    facturadas.map((c) => c.fields.IdInterno_CP).filter(Boolean) as string[],
  );
  const sumNotasCredito = (ncQ.data ?? [])
    .filter(
      (c) =>
        c.fields.Status_P === 'Nota de Credito' &&
        // Sólo las emitidas: una NC pendiente (sin CAE/número) todavía no acreditó nada.
        Boolean(c.fields.NroFactura_CP) &&
        // Las NC de un ciclo anterior (la factura se acreditó total y se volvió a facturar)
        // quedan `nc_superseded`: ya se descontaron de aquella factura, no de la nueva.
        c.fields.NcSuperseded_CP !== 'SI' &&
        c.fields.NotaCreditoDeId_CP != null &&
        attachedFacturaPks.has(c.fields.NotaCreditoDeId_CP),
    )
    .reduce((acc, c) => acc + montoNcEnMonedaVenta(c.fields), 0);
  // Una NC parcial es un DESCUENTO: la venta termina facturada por menos y lo
  // acreditado NO vuelve al saldo — ver `calcSaldoAFacturar`.
  const { totalFacturable, sumFacturadoNeto, saldo } = calcSaldoAFacturar({
    ventaTotal: ventaTotal ?? 0,
    sumFacturado,
    sumNotasCredito,
  });
  const saldoAFacturar = ventaTotal != null && ventaTotal > 0 ? saldo : null;
  // All rows pre-filter — used to decide whether the Anticipada/Final
  // selector is meaningful at all (it's only useful when the venta
  // has at least one MX two-stage Anticipo cobro).
  const allRows = facturadas.flatMap((c) => buildFacturaRows(c, isMx));
  const hasTwoStage = allRows.some((r) => r.etapa !== null);

  // Default to Final — it's what AR users implicitly see, and the
  // typical MX use case is verifying the canonical fiscal PDF. The
  // selector only renders when two-stage data is present (MX
  // Anticipos), otherwise it would be a no-op control.
  const [stageFilter, setStageFilter] = useState<StageFilter>('final');
  const rows = hasTwoStage
    ? allRows.filter((r) => {
        if (stageFilter === 'ambas') return true;
        // Single-stage rows (etapa===null) are AR / pago-final cobros.
        // Always include them regardless of the filter — the filter
        // toggles MX two-stage rows only.
        if (r.etapa === null) return true;
        return r.etapa === stageFilter;
      })
    : allRows;
  const showEtapa = rows.some((r) => r.etapa !== null);

  // Notas de Crédito (histórico) — todas las NC emitidas de la venta (parciales
  // y totales), en su propia pestaña. Excluye las neutralizadas (Status
  // 'Anulado' = rechazadas/saldo-excedido). AR-only: en MX `fullList` viene
  // vacío. La factura asociada usa el número snapshotteado al emitir
  // (FacturaAsociadaNro_CP) — sobrevive al reopen que limpia el nº del padre.
  const ncs = fullList.filter((c) => (c.fields.Status_P ?? '') === 'Nota de Credito');
  const [tab, setTab] = useState<'facturas' | 'ncs'>('facturas');

  return (
    <>
    <Dialog open onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-6xl">
        <DialogHeader>
          {/* Inline header: title sits side-by-side with the MX two-
              stage selector so the filter doesn't claim its own row.
              The control hides itself when the dataset has no MX
              Anticipo rows (AR ventas + MX pago-final ventas), and on
              narrow screens it wraps below the title via flex-wrap. */}
          <div className="flex flex-wrap items-center gap-3">
            <DialogTitle>Facturas cargadas</DialogTitle>
            {hasTwoStage && !comprobantesQ.isPending && !comprobantesQ.isError && (
              <StageFilterControl value={stageFilter} onChange={setStageFilter} />
            )}
          </div>
          <DialogDescription>
            Facturas emitidas de esta venta. Incluye las de cobros anulados, que siguen vigentes ante ARCA hasta que se les emita una Nota de Crédito.
          </DialogDescription>
        </DialogHeader>
        <DialogBody>
          {/* Pestañas Facturas | Notas de Crédito (histórico). Sólo aparecen
              cuando hay NCs (AR): MX y AR-sin-NC ven la vista de facturas tal
              cual. */}
          {ncs.length > 0 && (
            <div
              role="tablist"
              aria-label="Facturas o Notas de Crédito"
              className="mb-3 inline-flex rounded-lg border bg-secondary p-1"
            >
              {(['facturas', 'ncs'] as const).map((t) => (
                <button
                  key={t}
                  role="tab"
                  type="button"
                  aria-selected={tab === t}
                  onClick={() => setTab(t)}
                  className={cn(
                    'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
                    tab === t
                      ? 'bg-background text-foreground shadow-sm'
                      : 'text-muted-foreground hover:text-foreground',
                  )}
                >
                  {t === 'facturas' ? 'Facturas' : `Notas de Crédito (${ncs.length})`}
                </button>
              ))}
            </div>
          )}
          {(ncs.length === 0 || tab === 'facturas') && (
          <>
          {comprobantesQ.isPending ? (
            <div className="flex h-40 items-center justify-center text-muted-foreground">
              <Loader2 className="h-5 w-5 animate-spin" aria-hidden />
            </div>
          ) : comprobantesQ.isError ? (
            <p className="rounded-md border border-destructive/30 bg-destructive/10 px-4 py-3 text-sm text-destructive">
              No se pudieron cargar las facturas.
            </p>
          ) : rows.length === 0 ? (
            <div className="flex flex-col items-center justify-center gap-2 px-4 py-12 text-sm text-muted-foreground">
              <FileText className="h-6 w-6" aria-hidden />
              {hasTwoStage && stageFilter !== 'ambas'
                ? `No hay facturas ${stageFilter === 'anticipada' ? 'anticipadas' : 'finales'} cargadas todavía.`
                : 'Esta venta no tiene facturas cargadas todavía.'}
            </div>
          ) : (
            <div className="overflow-hidden rounded-xl border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    {/* Etapa anchors the leftmost column — status-like
                        columns scan faster on the left. Hidden when
                        the dataset has no two-stage rows so AR users
                        keep the original 5-column layout. */}
                    {showEtapa && (
                      <th className="px-3 py-2 font-semibold">Etapa</th>
                    )}
                    <th className="px-3 py-2 font-semibold">Fecha</th>
                    <th className="px-3 py-2 font-semibold">Método</th>
                    <th className="px-3 py-2 font-semibold">Nº Factura</th>
                    <th className="w-32 px-3 py-2 text-right font-semibold">Monto</th>
                    <th className="px-3 py-2 font-semibold">Factura</th>
                    {isAr && <th className="px-3 py-2 font-semibold">Acciones</th>}
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {rows.map((r) => (
                    <FacturaRow
                      key={`${r.comp.id}-${r.etapa ?? 'final'}`}
                      row={r}
                      moneda={moneda}
                      showEtapa={showEtapa}
                      isAr={isAr}
                      onGenerarNC={(id, nro) => setNcFor({ id, nro })}
                      canRegenerar={canRegenerar}
                      regenerating={regeneratingId === r.comp.id}
                      onRegenerar={handleRegenerar}
                    />
                  ))}
                </tbody>
              </table>
            </div>
          )}
          {/* Historial: facturas anuladas por una NC completa. Reabiertas
              (facturado=NO) → aparecen de nuevo en la cola de Facturación para
              re-emitir; acá dejamos la traza de qué NC las anuló. */}
          {anuladasPorNc.length > 0 && (
            <div className="mt-3 space-y-1.5 rounded-lg border border-rose-200 bg-rose-50/60 px-4 py-3">
              <p className="text-xs font-semibold uppercase tracking-wide text-rose-800">
                Facturas anuladas por Nota de Crédito
              </p>
              <ul className="space-y-1">
                {anuladasPorNc.map(({ parent, ncs }) => {
                  const f = parent.fields;
                  const monto = parseMoney(f.Monto_CP) ?? 0;
                  const ncNros = ncs
                    .map((n) => n.fields.NroFactura_CP)
                    .filter(Boolean)
                    .join(', ');
                  return (
                    <li
                      key={parent.id}
                      className="flex flex-wrap items-center gap-x-2 gap-y-0.5 text-xs text-rose-900"
                    >
                      <span>{f.FechaPago_CP || f.Fecha_CP || '—'}</span>
                      <span className="text-rose-300">·</span>
                      <span>{cajaBase(f.MedioPago_CP ?? '') || '—'}</span>
                      <span className="text-rose-300">·</span>
                      <span className="tabular-nums">{formatMoney(monto, moneda)}</span>
                      <span className="ml-auto font-medium">
                        Anulada por NC {ncNros || '—'} · pendiente de re-facturación
                      </span>
                    </li>
                  );
                })}
              </ul>
            </div>
          )}
          {/* Saldo a facturar (informativo): total de la venta − Σ ya facturado.
              Al pie para no empujar la tabla hacia abajo. */}
          {!comprobantesQ.isPending && !comprobantesQ.isError && saldoAFacturar != null && (
            <div className="mt-3 flex flex-wrap items-center gap-x-6 gap-y-1 rounded-lg border bg-muted/30 px-4 py-2.5 text-sm">
              <span
                title={
                  sumNotasCredito > 0
                    ? `Total de la venta ${formatMoney(ventaTotal ?? 0, moneda)} − Notas de Crédito ${formatMoney(sumNotasCredito, moneda)}`
                    : undefined
                }
              >
                {sumNotasCredito > 0 ? 'Total a facturar:' : 'Total de la venta:'}{' '}
                <strong className="tabular-nums">
                  {formatMoney(totalFacturable, moneda)}
                </strong>
              </span>
              <span
                title={
                  sumNotasCredito > 0
                    ? `Neto de Notas de Crédito: −${formatMoney(sumNotasCredito, moneda)}`
                    : undefined
                }
              >
                Facturado:{' '}
                <strong className="tabular-nums">{formatMoney(sumFacturadoNeto, moneda)}</strong>
              </span>
              <span className="ml-auto">
                Saldo a facturar:{' '}
                <strong
                  className={`tabular-nums ${
                    saldoAFacturar > 0.05 ? 'text-amber-700' : 'text-emerald-700'
                  }`}
                >
                  {formatMoney(saldoAFacturar, moneda)}
                </strong>
              </span>
            </div>
          )}
          </>
          )}
          {ncs.length > 0 && tab === 'ncs' && (
            <div className="overflow-hidden rounded-xl border">
              <table className="w-full text-sm">
                <thead className="bg-muted text-left text-xs uppercase tracking-wide text-muted-foreground">
                  <tr>
                    <th className="px-3 py-2 font-semibold">Fecha</th>
                    <th className="px-3 py-2 font-semibold">Nº Nota de Crédito</th>
                    <th className="px-3 py-2 font-semibold">Factura asociada</th>
                    <th className="w-32 px-3 py-2 text-right font-semibold">Monto</th>
                    <th className="px-3 py-2 font-semibold">PDF</th>
                  </tr>
                </thead>
                <tbody className="divide-y">
                  {ncs.map((nc) => {
                    const f = nc.fields;
                    const asociada =
                      f.FacturaAsociadaNro_CP ||
                      (f.NotaCreditoDeId_CP ? byPk.get(f.NotaCreditoDeId_CP)?.fields.NroFactura_CP : null) ||
                      '—';
                    const hasFile = Boolean(f.FacturaDriveItemID_CP && f.FacturaFileName_CP);
                    return (
                      <tr key={nc.id} className="hover:bg-muted/30">
                        <td className="px-3 py-2 align-middle text-foreground">
                          {f.FechaPago_CP || f.Fecha_CP || '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 align-middle text-foreground">
                          {f.NroFactura_CP || '—'}
                        </td>
                        <td className="whitespace-nowrap px-3 py-2 align-middle text-muted-foreground">
                          {asociada}
                        </td>
                        <td className="px-3 py-2 text-right align-middle">
                          <MoneyCell amount={montoNcEnMonedaVenta(f)} moneda={moneda} />
                        </td>
                        <td className="max-w-[20rem] px-3 py-2 align-middle">
                          {hasFile ? (
                            <div className="max-w-full">
                              <FileLink
                                entityType="NotaCreditoVenta"
                                entityId={nc.id}
                                fileName={f.FacturaFileName_CP ?? ''}
                                allowDelete={false}
                                compact
                              />
                            </div>
                          ) : (
                            <span
                              className="text-xs text-muted-foreground"
                              title="La NC no tiene un PDF adjunto (emitida antes de esta versión o falló el adjunto). Podés re-emitir sólo si hace falta."
                            >
                              PDF no disponible
                            </span>
                          )}
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}
        </DialogBody>
        <DialogFooter>
          <Button type="button" variant="outline" onClick={onClose}>
            Cerrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
    {ncFor && (
      <GenerarNotaCreditoDialog
        facturaReceiptId={ncFor.id}
        nroFactura={ncFor.nro}
        onClose={() => setNcFor(null)}
      />
    )}
    </>
  );
}

type FacturaRowData = {
  comp: ComprobantePagoItem;
  /**
   * `'anticipada'` → MX Factura Anticipada row (Anticipo cobro).
   * `'final'`      → MX Factura Final row (Anticipo cobro with both
   *                  stages); also used when the Final isn't uploaded
   *                  yet but we want a placeholder row for it.
   * `null`         → single-stage factura (AR + MX pago final). The
   *                  Etapa column hides itself when every row in the
   *                  dataset is single-stage.
   */
  etapa: 'anticipada' | 'final' | null;
  fileName: string | null;
  driveItemId: string | null;
  nroFactura: string | null;
  /** Empty-state copy for the file cell when neither file is attached. */
  pendingPlaceholder: boolean;
};

function buildFacturaRows(comp: ComprobantePagoItem, isMx: boolean): FacturaRowData[] {
  const f = comp.fields;
  const isAnticipo =
    (f.EsPagoFinal_CP ?? '').toString().trim().toUpperCase() !== 'SI';
  const isInterna = f.EsFacturaInterna_CP === 'SI';
  const hasAnticipada = Boolean(f.FacturaAnticipadaFileName_CP);
  const hasFinal = Boolean(f.FacturaFileName_CP);
  // Two-stage flow only applies to MX anticipos that aren't the auto-
  // generated Factura Interna. The Anticipada column lives on
  // sales_payment_receipts unconditionally, but only the Nova-flow
  // anticipos ever populate it. Gated to MX: AR cobros leave
  // `EsPagoFinal_CP` unset and would otherwise misclassify as anticipos.
  const twoStage = isMx && isAnticipo && !isInterna && (hasAnticipada || hasFinal);
  if (!twoStage) {
    return [
      {
        comp,
        etapa: null,
        fileName: f.FacturaFileName_CP ?? null,
        driveItemId: f.FacturaDriveItemID_CP ?? null,
        nroFactura: f.NroFactura_CP ?? null,
        pendingPlaceholder: !hasFinal,
      },
    ];
  }
  const rows: FacturaRowData[] = [];
  rows.push({
    comp,
    etapa: 'anticipada',
    fileName: f.FacturaAnticipadaFileName_CP ?? null,
    driveItemId: f.FacturaAnticipadaDriveItemID_CP ?? null,
    nroFactura: f.NroFacturaAnticipada_CP ?? null,
    pendingPlaceholder: !hasAnticipada,
  });
  rows.push({
    comp,
    etapa: 'final',
    fileName: f.FacturaFileName_CP ?? null,
    driveItemId: f.FacturaDriveItemID_CP ?? null,
    nroFactura: f.NroFactura_CP ?? null,
    pendingPlaceholder: !hasFinal,
  });
  return rows;
}

function FacturaRow({
  row,
  moneda,
  showEtapa,
  isAr,
  onGenerarNC,
  canRegenerar,
  regenerating,
  onRegenerar,
}: {
  row: FacturaRowData;
  moneda: Moneda;
  showEtapa: boolean;
  isAr: boolean;
  onGenerarNC: (facturaReceiptId: string, nroFactura: string) => void;
  canRegenerar: boolean;
  regenerating: boolean;
  onRegenerar: (receiptId: string, entityType: EntityType) => void;
}) {
  const { comp, etapa, fileName, nroFactura, pendingPlaceholder } = row;
  const f = comp.fields;
  const medio = cajaBase(f.MedioPago_CP ?? ''); // caja base, ver lib/cajasEfectivo
  const isUsdMedio =
    cajaBase(medio) === 'Efectivo Dolar' ||
    medio === 'Transferencia Galicia Dolar' ||
    medio === 'Transferencia Dolar';
  const displayAmount =
    moneda === 'ARS' && isUsdMedio
      ? parseMoney(f.Pesos_CP) ?? parseMoney(f.Monto_CP) ?? 0
      : parseMoney(f.Monto_CP) ?? 0;
  // AR: the vendor's classification — the "Pago Final" tag set at RegistrarPago
  // (EsPagoFinal_CP). MX uses the Etapa column instead, so AR only.
  const esPagoFinalTag = (f.EsPagoFinal_CP ?? '').toString().trim().toUpperCase() === 'SI';
  const arTipo = isAr ? (esPagoFinalTag ? 'final' : 'anticipo') : null;
  const hasFile = Boolean(row.driveItemId && fileName);
  // Con varias facturas en la venta hace falta poder distinguirlas de un
  // vistazo: cuál salió por Facturación Previa (plata no cobrada) y cuál
  // quedó sobre un cobro anulado — la factura sigue viva ante ARCA y es la
  // que necesita Nota de Crédito.
  const cobroAnulado = (f.Status_P ?? '').trim() === 'Anulado';
  const esPrevia = f.FacturacionPrevia_CP === 'SI';
  const entityType = etapa === 'anticipada' ? 'FacturaVentaAnticipada' : 'FacturaVenta';
  return (
    <tr className="hover:bg-muted/30">
      {showEtapa && (
        <td className="px-3 py-2 align-middle">
          {etapa === 'anticipada' ? (
            <span className="rounded-full bg-amber-100 px-2 py-0.5 text-xs font-medium text-amber-900">
              Anticipada
            </span>
          ) : etapa === 'final' ? (
            <span className="rounded-full bg-emerald-100 px-2 py-0.5 text-xs font-medium text-emerald-900">
              Final
            </span>
          ) : (
            <span className="text-xs text-muted-foreground">—</span>
          )}
        </td>
      )}
      <td className="px-3 py-2 align-middle text-foreground">
        {f.FechaPago_CP || f.Fecha_CP || '—'}
      </td>
      <td className="px-3 py-2 align-middle text-foreground">{medio || '—'}</td>
      <td className="whitespace-nowrap px-3 py-2 align-middle text-muted-foreground">
        {/* Nº + etiqueta en la MISMA fila (antes flex-col los apilaba). */}
        <div className="flex items-center gap-2">
          <span>{nroFactura || '—'}</span>
          {arTipo && (
            <span
              className={`rounded-full px-2 py-0.5 text-[11px] font-medium ${
                arTipo === 'final'
                  ? 'bg-emerald-100 text-emerald-900'
                  : 'bg-amber-100 text-amber-900'
              }`}
              title={
                arTipo === 'final'
                  ? 'El vendedor marcó este cobro como Pago Final.'
                  : 'El vendedor marcó este cobro como Anticipo (sin Pago Final).'
              }
            >
              {arTipo === 'final' ? 'Final' : 'Anticipo'}
            </span>
          )}
          {esPrevia && (
            <span
              className="rounded-full bg-sky-100 px-2 py-0.5 text-[11px] font-medium text-sky-900"
              title="Factura Anticipada: la factura se emitió antes de cobrar el pago."
            >
              FA
            </span>
          )}
          {cobroAnulado && (
            <span
              className="rounded-full bg-rose-100 px-2 py-0.5 text-[11px] font-medium text-rose-900"
              title="El cobro fue anulado, pero la factura sigue vigente ante ARCA. Emitile una Nota de Crédito para darla de baja."
            >
              Cobro anulado
            </span>
          )}
        </div>
      </td>
      <td className="px-3 py-2 text-right align-middle">
        <MoneyCell amount={displayAmount} moneda={moneda} />
      </td>
      <td className="max-w-[20rem] px-3 py-2 align-middle">
        {hasFile ? (
          // Wrap FileLink in a width-constrained div so the inner
          // truncate engages — the table cell otherwise auto-sizes
          // to the filename and pushes everything past the dialog.
          <div className="max-w-full">
            <FileLink
              entityType={entityType}
              entityId={comp.id}
              fileName={fileName ?? ''}
              allowDelete={false}
              compact
            />
          </div>
        ) : pendingPlaceholder ? (
          <span
            className="text-xs text-muted-foreground"
            title={
              etapa === 'anticipada'
                ? 'La Factura Anticipada no fue cargada todavía.'
                : etapa === 'final'
                  ? 'La Factura Final no fue cargada todavía.'
                  : 'Administración marcó este pago como con factura, pero el archivo aún no fue cargado en el módulo de Facturación.'
            }
          >
            Pendiente de carga
          </span>
        ) : (
          <span className="text-xs text-muted-foreground">—</span>
        )}
      </td>
      {/* AR only: emit a Nota de Crédito against this issued factura. Own
          column so the button sits beside the factura, not stacked under it. */}
      {isAr && (
        <td className="whitespace-nowrap px-3 py-2 align-middle">
          {/* Gate: el NÚMERO, no el PDF. `pendingPlaceholder` sólo dice que el
              archivo no se adjuntó, y una Nota de Crédito no lo necesita — le
              alcanza con el comprobante al que referencia. Si emitimos el CAE
              y el upload del PDF falla (pasa: el attach tiene 3 reintentos),
              la factura existe ante ARCA y tiene que poder anularse igual;
              atarlo al archivo dejaba ese caso sin salida. */}
          {/* Emitir la NC es admin/administración (el endpoint lo exige): a un
              vendedor el botón sólo le daba error. `canRegenerar` es ese mismo
              gate (AR + admin/administración). */}
          {nroFactura && canRegenerar ? (
            <div className="flex items-center gap-1.5">
              <Button
                type="button"
                variant="outline"
                size="sm"
                // Igual alto que el FileLink compacto de la columna Factura
                // (iconos h-6 + py-1 + borde = 34px), para que la fila no quede despareja.
                className="h-[34px] px-2 text-xs"
                onClick={() => onGenerarNC(comp.id, nroFactura)}
              >
                Generar NC
              </Button>
              {/* Regenerar la representación impresa con el N° de Serie /
                  Despacho cargados en el despacho. Sólo facturas ARCA con
                  archivo; el server rechaza las cargas manuales / sin CAE. */}
              {canRegenerar && hasFile && (
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  className="h-[34px] gap-1.5 px-2 text-xs"
                  disabled={regenerating}
                  title="Regenerar el PDF con el N° de Serie / Despacho"
                  onClick={() => onRegenerar(comp.id, entityType)}
                >
                  {regenerating ? (
                    <Loader2 className="h-3.5 w-3.5 animate-spin" aria-hidden />
                  ) : (
                    <RefreshCw className="h-3.5 w-3.5" aria-hidden />
                  )}
                  Regenerar
                </Button>
              )}
            </div>
          ) : null}
        </td>
      )}
    </tr>
  );
}

function StageFilterControl({
  value,
  onChange,
}: {
  value: StageFilter;
  onChange: (next: StageFilter) => void;
}) {
  const options: Array<{ key: StageFilter; label: string }> = [
    { key: 'final', label: 'Final' },
    { key: 'anticipada', label: 'Anticipada' },
    { key: 'ambas', label: 'Ambas' },
  ];
  return (
    <div
      role="tablist"
      aria-label="Filtro de etapa de factura"
      className="inline-flex shrink-0 self-start rounded-lg border bg-secondary p-1"
    >
      {options.map((opt) => {
        const active = opt.key === value;
        return (
          <button
            key={opt.key}
            role="tab"
            type="button"
            aria-selected={active}
            onClick={() => onChange(opt.key)}
            className={cn(
              'rounded-md px-3 py-1.5 text-sm font-medium transition-colors',
              active
                ? 'bg-background text-foreground shadow-sm'
                : 'text-muted-foreground hover:text-foreground',
            )}
          >
            {opt.label}
          </button>
        );
      })}
    </div>
  );
}

